import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import {
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Settings,
  Smartphone,
  Monitor,
  ArrowRightLeft,
  Loader2,
  ExternalLink
} from 'lucide-react';

import clsx from 'clsx';

interface GoogleTaskList {
  id: string;
  title: string;
  updated: string;
  synced: boolean;
  visionboard_board_id?: string;
  sync_direction?: string;
}

export default function GoogleTasksIntegrationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showSettings, setShowSettings] = useState(false);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      handleOAuthCallback(code);
    }
  }, [searchParams]);

  // Get integration status
  const { data: status = {}, isLoading: statusLoading } = useQuery({
    queryKey: ['google-tasks-status'],
    queryFn: () => api.getGoogleTasksStatus(),
  });

  // Get task lists (only if connected)
  const { data: taskLists = {}, isLoading: listsLoading } = useQuery({
    queryKey: ['google-tasks-lists'],
    queryFn: () => api.getGoogleTasksLists(),
    enabled: (status as any)?.connected,
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: (code: string) => api.connectGoogleTasks(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-tasks-status'] });
      queryClient.invalidateQueries({ queryKey: ['google-tasks-lists'] });
      // Clear URL params
      navigate('/settings/integrations/google-tasks', { replace: true });
    },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: (options?: { list_id?: string; force?: boolean }) =>
      api.syncGoogleTasks(options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-tasks-lists'] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => api.disconnectGoogleTasks(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-tasks-status'] });
      setShowSettings(false);
    },
  });

  const handleOAuthCallback = async (code: string) => {
    try {
      await connectMutation.mutateAsync(code);
    } catch (error) {
      console.error('OAuth callback error:', error);
    }
  };

  const handleConnect = () => {
    const oauthUrl = (status as any)?.oauth_url;
    if (oauthUrl) {
      window.location.href = oauthUrl;
    } else {
      alert('Google OAuth is not configured. Please contact administrator to set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in the backend.');
      console.error('OAuth URL not available. Status:', status);
    }
  };

  const handleSync = (listId?: string) => {
    syncMutation.mutate({ list_id: listId, force: true });
  };

  const handleDisconnect = () => {
    if (confirm('Are you sure you want to disconnect Google Tasks? This will remove all sync mappings.')) {
      disconnectMutation.mutate();
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Google Tasks Integration</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Sync your Google Tasks with VisionBoard for the complete productivity experience
          </p>
        </div>
        {(status as any)?.connected && (
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="btn-secondary"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        )}
      </div>

      {/* Connection Status */}
      <div className="card p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className={clsx(
            'w-12 h-12 rounded-full flex items-center justify-center',
            (status as any)?.connected
              ? 'bg-success-100 text-success-600'
              : 'bg-neutral-100 text-neutral-400'
          )}>
            {(status as any)?.connected ? (
              <CheckCircle2 className="w-6 h-6" />
            ) : (
              <AlertCircle className="w-6 h-6" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {(status as any)?.connected ? 'Connected' : 'Not Connected'}
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {(status as any)?.connected
                ? `${(status as any).stats?.synced_lists || 0} lists synced, ${(status as any).stats?.total_synced_tasks || 0} tasks`
                : 'Connect your Google Tasks to start syncing'
              }
            </p>
          </div>
        </div>

        {!(status as any)?.connected ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                How it works:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800 dark:text-blue-200">
                <div className="flex items-start gap-2">
                  <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Mobile:</strong> Use Google Tasks app for quick task capture on the go
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Monitor className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Desktop:</strong> Use VisionBoard for strategic planning and OKR alignment
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ArrowRightLeft className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Sync:</strong> Changes in either app automatically sync to the other
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Alignment:</strong> Link tasks to your OKRs for the big picture view
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleConnect}
              disabled={connectMutation.isPending}
              className="btn-primary w-full"
            >
              {connectMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  Connect Google Tasks
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => handleSync()}
              disabled={syncMutation.isPending}
              className="btn-secondary"
            >
              {syncMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync Now
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              className="btn-danger"
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {/* Task Lists */}
      {(status as any)?.connected && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Synced Task Lists</h2>
            <span className="text-sm text-neutral-500">
              {(taskLists as any)?.data?.length || 0} lists found
            </span>
          </div>

          {listsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="space-y-3">
              {(taskLists as any)?.data?.map((list: GoogleTaskList) => (
                <div
                  key={list.id}
                  className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      'w-3 h-3 rounded-full',
                      list.synced ? 'bg-success-500' : 'bg-neutral-300'
                    )} />
                    <div>
                      <h3 className="font-medium">{list.title}</h3>
                      <p className="text-sm text-neutral-500">
                        {list.synced
                          ? `Synced to VisionBoard board`
                          : 'Not synced'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {list.synced && (
                      <span className="text-xs bg-success-100 text-success-700 px-2 py-1 rounded">
                        ↕️ Bi-directional
                      </span>
                    )}
                    <button
                      onClick={() => handleSync(list.id)}
                      disabled={syncMutation.isPending}
                      className="btn-sm btn-secondary"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (status as any)?.connected && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Sync Settings</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Sync Frequency</label>
              <select className="input">
                <option value="5min">Every 5 minutes</option>
                <option value="15min">Every 15 minutes</option>
                <option value="1hour">Every hour</option>
                <option value="manual">Manual only</option>
              </select>
            </div>

            <div>
              <label className="label">Conflict Resolution</label>
              <select className="input">
                <option value="google_wins">Google Tasks wins</option>
                <option value="visionboard_wins">VisionBoard wins</option>
                <option value="manual">Ask me each time</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="preserve-vb-data" defaultChecked />
              <label htmlFor="preserve-vb-data" className="text-sm">
                Preserve VisionBoard data (KR links, subtasks) when syncing
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button className="btn-secondary" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
              <button className="btn-primary">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {connectMutation.isSuccess && (
        <div className="bg-success-50 text-success-700 p-4 rounded-lg">
          ✅ Google Tasks connected successfully! Your task lists are now syncing.
        </div>
      )}

      {syncMutation.isSuccess && (
        <div className="bg-success-50 text-success-700 p-4 rounded-lg">
          ✅ Sync completed successfully!
        </div>
      )}

      {(connectMutation.isError || syncMutation.isError || disconnectMutation.isError) && (
        <div className="bg-danger-50 text-danger-700 p-4 rounded-lg">
          ❌ Something went wrong. Please try again.
        </div>
      )}
    </div>
  );
}